import type { TrackedRect } from "@/lib/guide/useTrackedRect";

/**
 * Highlight ring around the target, above the dim backdrop. A single fixed,
 * pointer-events-none div drawing a bright violet ring + soft glow, with the
 * `attention-pulse` keyframe. Padding and corner radius are supplied by the
 * caller (derived from the element's size + computed radius) so the ring hugs
 * a small pill button as tightly as it frames a large list.
 */
export function GuideSpotlight({
  rect,
  radius,
  padding,
  reducedMotion,
}: Readonly<{
  rect: TrackedRect;
  radius: number;
  padding: number;
  reducedMotion: boolean;
}>) {
  const borderRadius = radius + padding;
  return (
    <div
      data-testid="guide-spotlight"
      aria-hidden="true"
      className="fixed z-[10050] pointer-events-none"
      style={{
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
        borderRadius,
        boxShadow:
          "0 0 0 2px hsl(var(--guide) / 0.95), 0 0 0 5px hsl(var(--guide) / 0.30)",
        transition: reducedMotion
          ? undefined
          : "top 180ms ease-out, left 180ms ease-out, width 180ms ease-out, height 180ms ease-out",
      }}
    >
      {!reducedMotion && (
        <div
          className="absolute inset-0 animate-attention-pulse"
          style={
            {
              borderRadius,
              "--attention-ring": "hsl(var(--guide) / 0.5)",
            } as React.CSSProperties
          }
        />
      )}
    </div>
  );
}
